import sortBy from "lodash/sortBy";
import { useMemo } from "react";
import { Table } from "react-bootstrap";
import { useParams } from "react-router-dom";

import { RouteCircle } from "../../../../components/RouteCircle";
import useCompetition from "../../../../hooks/useCompetition";
import {
  ApiCompetition,
  ApiCompetitionEntry,
  ApiCompetitionScorecardAttemptGroupData,
  ApiRoute,
} from "../../../../types";
import getCompetitionRouteDisplay from "../../../../utils/getCompetitionRouteDisplay";
import { getNumberWithOrdinal } from "../../../../utils/getNumberWithOrdinal";
import { FlashedSvg, TickedSvg, ZoneSvg } from "../../../../components/icons";

type Props = { entry: ApiCompetitionEntry; routes: ApiRoute[] };

const TryIcon = () => <div className="display-6 me-1">−</div>;
const ZoneIcon = () => <div className="display-6 me-1">Z</div>;
const Z1Icon = () => <div className="display-6 me-1">Z1</div>;
const Z2Icon = () => <div className="display-6 me-1">Z2</div>;
const TopIcon = () => <div className="display-6 me-1">T</div>;

const getTryCounts = (
  attemptsGroup: ApiCompetitionScorecardAttemptGroupData[]
) => {
  let triesToTop = 0;
  let triesToZone = 0;

  for (let i = 0; i < attemptsGroup.length; ++i) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const attempt = attemptsGroup[i]!;
    if (attempt.success) {
      triesToTop = i + 1;
      triesToZone = Math.max(triesToZone, triesToTop);
      break;
    }
    if (attempt.segmentsCount && triesToZone === 0) {
      triesToZone = i + 1;
    }
  }
  return { triesToTop, triesToZone };
};

export const EntryOverallScore = ({
  competition,
  entry,
}: {
  competition: ApiCompetition;
  entry: ApiCompetitionEntry;
}) => {
  if (competition.scoringSystem === "IFSC_SPORT") {
    return <>{entry.score.toFixed(2)} pts</>;
  } else if (competition.scoringSystem !== "IFSC_RANKING") {
    return <>{entry.score.toFixed(0)} pts</>;
  } else {
    return (
      <>
        <span className="me-3">
          {entry.scoreTuple[0]}
          <small className="fw-normal" style={{ marginLeft: 2 }}>
            T
          </small>
        </span>{" "}
        <span className="me-3">
          {entry.scoreTuple[1]}
          <small className="fw-normal" style={{ marginLeft: 2 }}>
            Z
          </small>
        </span>{" "}
        <span className="me-3">
          {-(entry.scoreTuple[2] || 0)}
          <small className="fw-normal" style={{ marginLeft: 2 }}>
            TA
          </small>
        </span>{" "}
        <span className="me-1">
          {-(entry.scoreTuple[3] || 0)}
          <small className="fw-normal" style={{ marginLeft: 2 }}>
            ZA
          </small>
        </span>
      </>
    );
  }
};

const EntryScorecard = ({ entry, routes }: Props) => {
  const attemptsByRoute =
    entry.stageScorecards?.[0]?.venueGroups?.[0]?.attempts;

  const { competitionUUID } = useParams();

  const { competition } = useCompetition(competitionUUID);

  const routesWithAttempts = useMemo(
    () =>
      sortBy(
        routes?.map((route) => {
          const display = getCompetitionRouteDisplay({
            route: route,
            routeGroups: route.competitionRouteGroups,
            categoryUUID: entry.category.uuid,
          });
          const attempts = attemptsByRoute?.find(
            (atts) => atts.route.uuid === route.uuid
          );
          return {
            ...route,
            display,
            attempts: attempts
              ? {
                  ...attempts,
                  ...getTryCounts(attempts.groupData),
                }
              : undefined,
          };
        }),
        (item) => item.display.number
      ),
    [attemptsByRoute, entry.category.uuid, routes]
  );

  return (
    <div style={{ marginRight: "-12px", marginLeft: "-12px" }}>
      <Table className="align-middle table-striped table-borderless">
        <thead>
          <tr>
            <th>Route</th>
            {competition?.scoringSystem === "IFSC_RANKING" ? (
              <>
                <th className="text-end">Top</th>
                <th className="text-end">Zone</th>
                <th className="text-end">Result</th>
              </>
            ) : (
              <>
                <th className="">Result</th>
                <th className="text-end">Score</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {routesWithAttempts.map((route) => {
            const attemptsToResult = route.attempts?.success
              ? route.attempts.triesToTop
              : route.attempts?.segmentsCount
              ? route.attempts.triesToZone
              : route.attempts?.count;
            return (
              <tr key={route.uuid}>
                <td className="">
                  <div className="d-flex">
                    <RouteCircle
                      colors={[route.colors]}
                      className="me-1 d-inline-block"
                    />
                    <strong>{route.display.name}</strong>
                  </div>
                </td>
                {competition?.scoringSystem === "IFSC_RANKING" ? (
                  <>
                    <td className="text-end">
                      {route.attempts?.triesToTop ?? "\u2013"}
                    </td>
                    <td className="text-end">
                      {route.attempts?.triesToZone ?? "\u2013"}
                    </td>
                    <td className="text-end">
                      {route.attempts?.success ? (
                        <TopIcon />
                      ) : route.attempts?.segmentsCount ? (
                        (route.segmentsCount || 0) > 1 ? (
                          route.attempts?.segmentsCount === 2 ? (
                            <Z2Icon />
                          ) : (
                            <Z1Icon />
                          )
                        ) : (
                          <ZoneIcon />
                        )
                      ) : (
                        <TryIcon />
                      )}
                    </td>
                  </>
                ) : competition?.scoringSystem === "IFSC_SPORT" ? (
                  <>
                    <td className="">
                      {route.attempts?.success
                        ? "TOP"
                        : route.attempts?.segmentsCount}
                      {route.attempts?.usedLastHold ? "+" : ""}
                    </td>
                    <td className="text-end">
                      {route.attempts?.score?.toFixed(2) ?? "\u2013"}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="">
                      <div className="d-inline-block">
                        {route.attempts?.success ? (
                          route.attempts.count === 1 ? (
                            <FlashedSvg />
                          ) : (
                            <TickedSvg />
                          )
                        ) : route.attempts?.segmentsCount ? (
                          <ZoneSvg />
                        ) : (
                          <span
                            className="d-inline-block"
                            style={{ width: 16, textAlign: "center" }}
                          >
                            –
                          </span>
                        )}
                      </div>
                      {attemptsToResult !== undefined ? (
                        route.attempts?.success ||
                        route.attempts?.segmentsCount ? (
                          <small className="ms-2">
                            {getNumberWithOrdinal(attemptsToResult)} try
                          </small>
                        ) : (
                          <small className="ms-2">
                            {attemptsToResult}{" "}
                            {attemptsToResult === 1 ? "try" : "tries"}
                          </small>
                        )
                      ) : null}
                    </td>
                    <td className="text-end">
                      {route.attempts?.score !== undefined
                        ? route.attempts.score.toFixed(0) + " pts"
                        : "\u2013"}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot className="table-dark">
          <tr>
            <th>Overall score</th>
            <th colSpan={3} className="text-end">
              <EntryOverallScore competition={competition} entry={entry} />
            </th>
          </tr>
        </tfoot>
      </Table>
    </div>
  );
};

export default EntryScorecard;
